import { Box, Button, LinearProgress, Slide, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { TriggeredHook } from "sonobello.utilities.react";

import { LeadResult } from "../../../../dtos/LeadResult";
import { Step } from "../../../../types/Step";
import { isMobileView } from "../../../../utils/Constants";
import { IFlowStepProps } from "../../../Types/IFlow";
import StepWrapper, { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import IMedicalQuestionnaireCompleteEvaluator from "../Types/IMedicalQualificationCompleteEvaluator";
import IMedicalQualificationNonCandidateEvaluator from "../Types/IMedicalQualificationNonCandidateEvaluator";
import { IMedicalStepProps } from "./FormSeriesMedicalStep";
import MedicalQuestionSequence from "../Compositions/MedicalQuestionSequence";

/** The configuration for the one-at-a-time medical step. Questions are defined internally. */
export interface IOneAtATimeMedicalStepConfig {
  /** Evaluates the full form for completeness and non-candidacy. */
  medicalQualificationEvaluator: IMedicalQualificationNonCandidateEvaluator & IMedicalQuestionnaireCompleteEvaluator;
  /** The step to navigate to after all questions are answered and the user is not a non-candidate. */
  nextStep: Step;
  /** A hook to be called when the user is disqualified as a non-candidate. */
  useCreateNonCandidateLeadResult: TriggeredHook<LeadResult, undefined, boolean>;
}

/** Renders medical questions one at a time with a directional slide transition.
 * The Back button returns to the previous question; Next advances until all questions are answered,
 * at which point it submits and evaluates for non-candidacy. */
const OneAtATimeMedicalStep: React.FC<IMedicalStepProps & IOneAtATimeMedicalStepConfig & IStepWrapperButtonProps> = ({
  medicalQualificationEvaluator,
  nextStep,
  router,
  value,
  onMedicalNoncandidateDisqualification,
  setMedicalConclusions,
  setMedicalFormState,
  useCreateNonCandidateLeadResult,
  NextButtonMobile,
  NextButtonDesktop
}) => {
  const isMobile = isMobileView();
  const flowStepProps = useOutletContext<IFlowStepProps>();
  const { execute: createNonCandidateLeadResult, result, isLoading } = useCreateNonCandidateLeadResult();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">("left");

  const isLastQuestion = currentIndex === MedicalQuestionSequence.length - 1;
  const isCurrentComplete = MedicalQuestionSequence[currentIndex].isComplete(value);
  const isAllComplete = medicalQualificationEvaluator.isComplete(value);

  // Track whether the question was already complete when we arrived, to avoid
  // auto-advancing past a previously-answered question on arrival or back-navigation.
  const wasAlreadyCompleteRef = useRef(false);
  useEffect(() => {
    wasAlreadyCompleteRef.current = MedicalQuestionSequence[currentIndex].isComplete(value);
  }, [currentIndex]);

  // Auto-advance only when the question was unanswered on arrival and is answered for the first time.
  useEffect(() => {
    if (!isCurrentComplete || isLastQuestion || wasAlreadyCompleteRef.current) return;
    const timer = setTimeout(() => {
      setSlideDirection("left");
      setCurrentIndex(i => i + 1);
    }, 400);
    return () => clearTimeout(timer);
  }, [isCurrentComplete]);

  const handleAdvanceQuestion = () => {
    setSlideDirection("left");
    setCurrentIndex(i => i + 1);
  };

  const handleSubmit = () => {
    if (medicalQualificationEvaluator.isNonCandidate(value)) {
      onMedicalNoncandidateDisqualification();
      createNonCandidateLeadResult();
    } else {
      flowStepProps.setStep(nextStep);
    }
  };

  const handleBack = () => {
    setSlideDirection("right");
    setCurrentIndex(i => i - 1);
  };

  useEffect(() => {
    if (result) router.goToNonCandidateView();
  }, [result]);

  const { Component } = MedicalQuestionSequence[currentIndex];

  return (
    <StepWrapper
      NextButtonMobile={NextButtonMobile}
      NextButtonDesktop={NextButtonDesktop}
      nextButtonLoading={isLoading}
      nextButtonLabel="Submit"
      onNext={handleSubmit}
      disabled={isLoading || !isLastQuestion || !isAllComplete}
    >
      <Box display="flex" justifyContent="center" width="100%">
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 600 }}>
          <Stack spacing={0.5}>
            <LinearProgress
              variant="determinate"
              value={((currentIndex + 1) / MedicalQuestionSequence.length) * 100}
              sx={{ borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary" align="right">
              {currentIndex + 1} / {MedicalQuestionSequence.length}
            </Typography>
          </Stack>

          <Box sx={{ overflow: "hidden" }}>
            <Slide key={currentIndex} direction={slideDirection} in timeout={250} mountOnEnter unmountOnExit>
              <Box sx={{ py: 0.5 }}>
                <Component
                  value={value}
                  onChange={s => setMedicalFormState(o => ({ ...o, ...s }))}
                  isMobile={isMobile}
                  setMedicalConclusions={setMedicalConclusions}
                  setMedicalFormState={setMedicalFormState}
                />
              </Box>
            </Slide>
          </Box>

          <Box display="flex" justifyContent={currentIndex > 0 ? "space-between" : "flex-end"} alignItems="center">
            {currentIndex > 0 && (
              <Button variant="outlined" size="small" onClick={handleBack} disabled={isLoading}>
                ← Previous
              </Button>
            )}
            {!isLastQuestion && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleAdvanceQuestion}
                disabled={!isCurrentComplete || isLoading}
              >
                Next question →
              </Button>
            )}
          </Box>
        </Stack>
      </Box>
    </StepWrapper>
  );
};

export default OneAtATimeMedicalStep;
